/**
 * @fileoverview AI Settings Service
 * @description Manages user AI preferences including BYOK (Bring Your Own Key)
 * @module services/core/ai-settings.service
 * @version 2.1.0 - Phase 4 Architecture
 *
 * Storage: Non-sensitive prefs in localStorage + mycelium_principals.preferences.ai.
 * BYOK: localStorage cache + `user_ai_credentials` via RPC (server quota fallback).
 */

import { DEFAULT_AI_SETTINGS } from '@flowstate/types';
import type { AICredentialStatus, AIModel, AIModelConfig, BoxAgentPreferences, UserAISettings } from '@flowstate/types';
import { supabase } from '@/integrations/supabase/client';
import { logger } from "@/lib/logger";
import {
  IService,
  ServiceResponse,
  registerService,
} from '@/services/core/service-registry';

const STORAGE_KEY = 'flowstate_ai_settings';

// ============================================================================
// TYPES
// ============================================================================

export interface ValidationResult {
  valid: boolean;
  message?: string;
}

export interface TestResult {
  success: boolean;
  message: string;
}

// ============================================================================
// AI SETTINGS SERVICE - Phase 4 Architecture
// ============================================================================

export interface IAISettingsService extends IService {
  getSettings(): ServiceResponse<UserAISettings>;
  saveSettings(settings: Partial<UserAISettings>): ServiceResponse<UserAISettings>;
  setGeminiKey(apiKey: string): Promise<ServiceResponse<UserAISettings>>;
  /** Push an existing localStorage BYOK key to the server (quota fallback). */
  syncLocalByokToServer(): Promise<ServiceResponse<UserAISettings>>;
  clearGeminiKey(): Promise<ServiceResponse<UserAISettings>>;
  setPreferredModel(model: AIModel): ServiceResponse<UserAISettings>;
  setModelConfig(config: Partial<AIModelConfig>): ServiceResponse<UserAISettings>;
  setOverride(task: keyof Required<UserAISettings>['overrides'], model: AIModel): ServiceResponse<UserAISettings>;
  resetToDefaults(): Promise<ServiceResponse<UserAISettings>>;
  validateGeminiKey(key: string): ValidationResult;
  validateOpenRouterKey(key: string): ValidationResult;
  /**
   * Set OpenRouter API key (local cache + server for inference).
   */
  setOpenRouterKey(apiKey: string): Promise<ServiceResponse<UserAISettings>>;
  /** Clear the OpenRouter API key (local + server) */
  clearOpenRouterKey(): Promise<ServiceResponse<UserAISettings>>;
  /**
   * Derive the current credential status from server RPCs and local settings.
   */
  getCredentialStatus(): Promise<AICredentialStatus>;
  /**
   * Loads AI preferences from `mycelium_principals.preferences.ai` and merges
   * with localStorage. Falls back to localStorage on auth/network error.
   * BYOK key material is never read from the server — only `has_my_gemini_api_key`.
   */
  syncFromPrincipal(): Promise<ServiceResponse<UserAISettings>>;
  /**
   * Merges non-sensitive AI preferences into `mycelium_principals.preferences`
   * via the `update_my_principal_preferences` RPC. Fire-and-forget safe.
   */
  syncToPrincipal(settings: UserAISettings): Promise<ServiceResponse<void>>;
}

/**
 * AI Settings Service - manages user AI preferences
 * Phase 4 Compliant - Implements IService with ServiceResponse wrappers
 */
export class AISettingsService implements IAISettingsService {
  readonly name = "AISettingsService";

  private normalizeSettings(settings: Partial<UserAISettings>): UserAISettings {
    const hasGeminiByok = !!settings.geminiApiKey || !!settings.geminiByokOnServer;
    const hasOpenRouterByok = !!settings.openrouterApiKey || !!settings.openrouterApiKeyOnServer;
    // 'flowstate' is a legacy stored value that used to mean platform-funded inference.
    const storedProvider = settings.provider === 'flowstate' ? 'gemini' : settings.provider;
    const provider = (storedProvider
      ?? ((hasGeminiByok || settings.useGoogleGemini ? 'gemini' : undefined))
      ?? (hasOpenRouterByok ? 'openrouter' : undefined))
      ?? DEFAULT_AI_SETTINGS.provider
      ?? 'gemini';

    return {
      ...DEFAULT_AI_SETTINGS,
      ...settings,
      provider,
      useOwnKey: hasGeminiByok || hasOpenRouterByok,
    };
  }

  async initialize(): Promise<void> {
    logger.info("AISettingsService initialized", { service: this.name });
  }

  async dispose(): Promise<void> {
    logger.info("AISettingsService disposed", { service: this.name });
  }

  /**
   * Get user's AI settings from localStorage
   */
  getSettings(): ServiceResponse<UserAISettings> {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { success: true, data: this.normalizeSettings(parsed) };
      }
      return { success: true, data: this.normalizeSettings(DEFAULT_AI_SETTINGS) };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to load AI settings";
      logger.error("Error loading AI settings", { error: errorMessage });
      return { success: true, data: this.normalizeSettings(DEFAULT_AI_SETTINGS) };
    }
  }

  /**
   * Save user's AI settings to localStorage
   */
  saveSettings(settings: Partial<UserAISettings>): ServiceResponse<UserAISettings> {
    try {
      const currentResult = this.getSettings();
      const current = currentResult.success ? currentResult.data : DEFAULT_AI_SETTINGS;

      const updated: UserAISettings = this.normalizeSettings({
        ...current,
        ...settings,
        updatedAt: new Date().toISOString(),
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return { success: true, data: updated };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to save AI settings";
      logger.error("Error saving AI settings", { error: errorMessage });
      return { success: false, error: errorMessage };
    }
  }

  private async persistGeminiKeyToServer(apiKey: string): Promise<ServiceResponse<void>> {
    const { error } = await supabase.rpc('set_my_gemini_api_key' as never, { p_key: apiKey } as never);
    if (error) {
      const msg = (error as { message?: string }).message ?? String(error);
      logger.error('Failed to persist Gemini BYOK to server', { error: msg });
      return { success: false, error: msg };
    }
    return { success: true, data: undefined };
  }

  private async clearGeminiKeyOnServer(): Promise<ServiceResponse<void>> {
    const { error } = await supabase.rpc('clear_my_gemini_api_key' as never);
    if (error) {
      const msg = (error as { message?: string }).message ?? String(error);
      logger.error('Failed to clear Gemini BYOK on server', { error: msg });
      return { success: false, error: msg };
    }
    return { success: true, data: undefined };
  }

  /**
   * Set Gemini API key (local cache + server for quota fallback)
   */
  async setGeminiKey(apiKey: string): Promise<ServiceResponse<UserAISettings>> {
    const validation = this.validateGeminiKey(apiKey);
    if (!validation.valid) {
      return { success: false, error: validation.message ?? "Invalid Gemini API key" };
    }

    const persisted = await this.persistGeminiKeyToServer(apiKey.trim());
    if (!persisted.success) {
      return { success: false, error: persisted.error };
    }

    return this.saveSettings({
      provider: 'gemini',
      geminiApiKey: apiKey.trim(),
      geminiByokOnServer: true,
      useOwnKey: true,
    });
  }

  /**
   * Sync local BYOK key to server (one-time migration for pre-persistence users).
   */
  async syncLocalByokToServer(): Promise<ServiceResponse<UserAISettings>> {
    const local = this.getSettings();
    const key = local.success ? local.data.geminiApiKey : undefined;
    if (!key) {
      return { success: false, error: 'No Gemini API key in local settings' };
    }
    return this.setGeminiKey(key);
  }

  /** Clear the Gemini API key (local + server) */
  async clearGeminiKey(): Promise<ServiceResponse<UserAISettings>> {
    const cleared = await this.clearGeminiKeyOnServer();
    if (!cleared.success) {
      return { success: false, error: cleared.error };
    }

    return this.saveSettings({
      provider: 'gemini',
      geminiApiKey: undefined,
      geminiByokOnServer: false,
      useOwnKey: false,
    });
  }

  /**
   * Set preferred model
   */
  setPreferredModel(model: AIModel): ServiceResponse<UserAISettings> {
    return this.saveSettings({ preferredModel: model });
  }

  /**
   * Update model configuration
   */
  setModelConfig(config: Partial<AIModelConfig>): ServiceResponse<UserAISettings> {
    const currentResult = this.getSettings();
    const current = currentResult.success ? currentResult.data : DEFAULT_AI_SETTINGS;

    return this.saveSettings({
      modelConfig: {
        ...current.modelConfig,
        ...config,
      },
    });
  }

  /**
   * Set task-specific override
   */
  setOverride(task: keyof Required<UserAISettings>['overrides'], model: AIModel): ServiceResponse<UserAISettings> {
    const currentResult = this.getSettings();
    const current = currentResult.success ? currentResult.data : DEFAULT_AI_SETTINGS;

    return this.saveSettings({
      overrides: {
        ...(current.overrides ?? DEFAULT_AI_SETTINGS.overrides),
        [task]: model,
      },
    });
  }

  /**
   * Validate Gemini API key format
   */
  validateGeminiKey(key: string): ValidationResult {
    if (!key) {
      return { valid: false, message: 'API key is required' };
    }

    if (key.length < 10) {
      return { valid: false, message: 'API key is too short' };
    }

    return { valid: true };
  }

  /**
   * Validate OpenRouter API key format
   */
  validateOpenRouterKey(key: string): ValidationResult {
    const trimmed = key.trim();
    if (!trimmed) {
      return { valid: false, message: 'API key is required' };
    }

    if (!trimmed.startsWith('sk-or-v1-') || trimmed.length < 40) {
      return { valid: false, message: 'OpenRouter keys start with "sk-or-v1-" and are at least 40 characters' };
    }

    return { valid: true };
  }

  private async persistOpenRouterKeyToServer(apiKey: string): Promise<ServiceResponse<void>> {
    const { error } = await supabase.rpc('set_my_openrouter_api_key' as never, { p_key: apiKey } as never);
    if (error) {
      const msg = (error as { message?: string }).message ?? String(error);
      logger.error('Failed to persist OpenRouter BYOK to server', { error: msg });
      return { success: false, error: msg };
    }
    return { success: true, data: undefined };
  }

  private async clearOpenRouterKeyOnServer(): Promise<ServiceResponse<void>> {
    const { error } = await supabase.rpc('clear_my_openrouter_api_key' as never);
    if (error) {
      const msg = (error as { message?: string }).message ?? String(error);
      logger.error('Failed to clear OpenRouter BYOK on server', { error: msg });
      return { success: false, error: msg };
    }
    return { success: true, data: undefined };
  }

  /**
   * Set OpenRouter API key (local cache + server)
   */
  async setOpenRouterKey(apiKey: string): Promise<ServiceResponse<UserAISettings>> {
    const validation = this.validateOpenRouterKey(apiKey);
    if (!validation.valid) {
      return { success: false, error: validation.message ?? 'Invalid OpenRouter API key' };
    }

    const persisted = await this.persistOpenRouterKeyToServer(apiKey.trim());
    if (!persisted.success) {
      return { success: false, error: persisted.error };
    }

    return this.saveSettings({
      provider: 'openrouter',
      openrouterApiKey: apiKey.trim(),
      openrouterApiKeyOnServer: true,
      useOwnKey: true,
    });
  }

  /** Clear the OpenRouter API key (local + server) */
  async clearOpenRouterKey(): Promise<ServiceResponse<UserAISettings>> {
    const cleared = await this.clearOpenRouterKeyOnServer();
    if (!cleared.success) {
      return { success: false, error: cleared.error };
    }

    return this.saveSettings({
      provider: 'openrouter',
      openrouterApiKey: undefined,
      openrouterApiKeyOnServer: false,
      useOwnKey: false,
    });
  }

  /**
   * Derive the current credential status from server RPCs.
   */
  async getCredentialStatus(): Promise<AICredentialStatus> {
    try {
      const { data, error } = await supabase.rpc('get_my_ai_credential_status' as never);
      if (error) {
        logger.warn('get_my_ai_credential_status failed', { error: error.message });
      }
      const value = data as AICredentialStatus | null;
      if (value && ['none', 'gemini_oauth', 'gemini_byok', 'openrouter_byok'].includes(value)) {
        return value;
      }
      return 'none';
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      logger.warn('getCredentialStatus failed', { error: message });
      return 'none';
    }
  }

  // ── Supabase sync ─────────────────────────────────────────────────────────

  /**
   * Loads AI preferences from `mycelium_principals.preferences.ai` and merges
   * with the localStorage cache.
   */
  async syncFromPrincipal(): Promise<ServiceResponse<UserAISettings>> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      const authUserId = userData.user?.id;
      if (!authUserId) return this.getSettings();

      const [
        { data, error },
        { data: hasGeminiServerKey },
        { data: hasOpenRouterServerKey },
        { data: credentialStatus },
      ] = await Promise.all([
        supabase
          .from('mycelium_principals')
          .select('preferences')
          .eq('kind', 'human')
          .eq('is_active', true)
          .eq('auth_user_id', authUserId)
          .maybeSingle(),
        supabase.rpc('has_my_gemini_api_key' as never),
        supabase.rpc('has_my_openrouter_api_key' as never),
        supabase.rpc('get_my_ai_credential_status' as never),
      ]);

      if (error || !data) {
        return this.getSettings();
      }

      const aiPrefs = (data.preferences as { ai?: Partial<UserAISettings> })?.ai ?? {};
      const localResult = this.getSettings();
      const local = localResult.success ? localResult.data : DEFAULT_AI_SETTINGS;
      const geminiByokOnServer = hasGeminiServerKey === true;
      const openrouterApiKeyOnServer = hasOpenRouterServerKey === true;
      const serverCredentialStatus = typeof credentialStatus === 'string'
        ? credentialStatus as AICredentialStatus
        : undefined;

      const merged = this.normalizeSettings({
        ...DEFAULT_AI_SETTINGS,
        ...aiPrefs,
        geminiApiKey: local.geminiApiKey,
        geminiByokOnServer,
        openrouterApiKey: local.openrouterApiKey,
        openrouterApiKeyOnServer,
        aiCredentialStatus: serverCredentialStatus ?? local.aiCredentialStatus,
        box: {
          ...(aiPrefs.box ?? {}),
          githubToken: local.box?.githubToken,
        },
      });

      localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
      return { success: true, data: merged };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      logger.error('AISettingsService.syncFromPrincipal failed', { error: msg });
      return this.getSettings();
    }
  }

  /**
   * Writes non-sensitive AI preferences to `mycelium_principals.preferences.ai`
   * via the `update_my_principal_preferences` SECURITY DEFINER RPC.
   */
  async syncToPrincipal(settings: UserAISettings): Promise<ServiceResponse<void>> {
    try {
      const {
        geminiApiKey: _key,
        geminiByokOnServer: _serverKey,
        openrouterApiKey: _orKey,
        openrouterApiKeyOnServer: _orServerKey,
        box,
        ...safeSettings
      } = settings;
      const safeBox: Omit<BoxAgentPreferences, 'githubToken'> | undefined = box
        ? { installCLIs: box.installCLIs }
        : undefined;

      const aiPrefs: Partial<UserAISettings> = {
        ...safeSettings,
        ...(safeBox !== undefined ? { box: safeBox } : {}),
      };

      const { error } = await supabase.rpc(
        'update_my_principal_preferences' as never,
        { p_preferences: { ai: aiPrefs } } as never,
      );

      if (error) {
        logger.error('AISettingsService.syncToPrincipal failed', { error: (error as { message?: string }).message });
        return { success: false, error: (error as { message?: string }).message ?? String(error) };
      }

      return { success: true, data: undefined };
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      logger.error('AISettingsService.syncToPrincipal failed', { error: msg });
      return { success: false, error: msg };
    }
  }

  // ── Reset ─────────────────────────────────────────────────────────────────

  /**
   * Reset to defaults (local + clear server BYOK)
   */
  async resetToDefaults(): Promise<ServiceResponse<UserAISettings>> {
    try {
      await Promise.all([
        this.clearGeminiKeyOnServer(),
        this.clearOpenRouterKeyOnServer(),
      ]);
      localStorage.removeItem(STORAGE_KEY);
      return { success: true, data: this.normalizeSettings(DEFAULT_AI_SETTINGS) };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to reset settings";
      logger.error("Error resetting AI settings", { error: errorMessage });
      return { success: false, error: errorMessage };
    }
  }
}

// Register singleton
registerService("AISettingsService", () => new AISettingsService());

// Export singleton instance
export const aiSettingsServiceInstance = new AISettingsService();

/** @deprecated Use aiSettingsServiceInstance or serviceRegistry */
export const aiSettingsService = aiSettingsServiceInstance;

export default aiSettingsService;
